import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  CircularProgress,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
  Collapse,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  Activity,
  ActivityCategory,
  ActivityStatus,
  RecurrenceType,
  RecurrenceRule,
  User,
  UserRole,
  RewardType,
} from "../../types";
import { useAuth } from "../../contexts/AuthContext";
import { createActivity } from "../../services/activities";
import { getFamilyMembers } from "../../services/family";

interface ActivityFormProps {
  activity?: Activity;
  onSubmit: (activity: Activity) => void;
  onCancel?: () => void;
}

const ActivityForm: React.FC<ActivityFormProps> = ({
  activity,
  onSubmit,
  onCancel,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Family members for assignment
  const [familyMembers, setFamilyMembers] = useState<User[]>([]);
  const [assignedTo, setAssignedTo] = useState<string>(
    activity?.userId || user?.userId || ""
  );

  // Form state
  const [title, setTitle] = useState(activity?.title || "");
  const [description, setDescription] = useState(activity?.description || "");
  const [category, setCategory] = useState<ActivityCategory>(
    (activity?.category as ActivityCategory) || ActivityCategory.CHORE
  );
  const [points, setPoints] = useState<number>(activity?.points || 10);
  const [rewardType, setRewardType] = useState<RewardType>(
    activity?.rewardType || RewardType.POINTS
  );
  const [rewardAmount, setRewardAmount] = useState<number>(
    activity?.rewardAmount || activity?.points || 10
  );
  const [dueDate, setDueDate] = useState<Date | null>(
    activity?.dueDate ? new Date(activity.dueDate) : null
  );

  // Recurrence state
  const [isRecurring, setIsRecurring] = useState<boolean>(
    !!activity?.recurrence && activity.recurrence.type !== RecurrenceType.NONE
  );
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>(
    activity?.recurrence?.type || RecurrenceType.NONE
  );
  const [recurrenceInterval, setRecurrenceInterval] = useState<number>(
    activity?.recurrence?.interval || 1
  );
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<Date | null>(
    activity?.recurrence?.endDate ? new Date(activity.recurrence.endDate) : null
  );
  const [dayOfWeek, setDayOfWeek] = useState<number>(
    activity?.recurrence?.dayOfWeek || new Date().getDay()
  );

  // Form validation errors
  const [formErrors, setFormErrors] = useState({
    title: "",
    description: "",
  });

  // Load family members for assignment dropdown
  useEffect(() => {
    const loadFamilyMembers = async () => {
      try {
        if (user) {
          const members = await getFamilyMembers(user.familyId);
          // Filter to only show active members (not pending invitations)
          const activeMembers = members.filter(
            (member: User) =>
              member.userId && !((member as any).status === "PENDING")
          );
          setFamilyMembers(activeMembers);
        }
      } catch (err) {
        console.error("Error loading family members:", err);
      }
    };

    loadFamilyMembers();
  }, [user]);

  const validateForm = (): boolean => {
    let isValid = true;
    const errors = {
      title: "",
      description: "",
    };

    if (!title.trim()) {
      errors.title = "Title is required";
      isValid = false;
    }

    if (!description.trim()) {
      errors.description = "Description is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (!user) {
        throw new Error("User not authenticated");
      }

      const activityData: Partial<Activity> = {
        activityId: activity?.activityId || "",
        familyId: user.familyId,
        userId: assignedTo || activity?.userId || user.userId,
        title,
        description,
        category,
        points, // Keep for backward compatibility
        rewardType,
        rewardAmount,
        status: activity?.status || ActivityStatus.PENDING,
        dueDate: dueDate?.toISOString() || undefined,
        createdAt: activity?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Add recurrence data if enabled
      if (isRecurring && recurrenceType !== RecurrenceType.NONE) {
        const recurrenceRule: RecurrenceRule = {
          type: recurrenceType,
          interval: recurrenceInterval,
          endDate: recurrenceEndDate?.toISOString(),
        };

        // Add day of week for weekly recurrence
        if (recurrenceType === RecurrenceType.WEEKLY) {
          recurrenceRule.dayOfWeek = dayOfWeek;
        }

        activityData.recurrence = recurrenceRule;
      }

      let result;
      if (activity) {
        // For update flow, we're just passing through to parent handler
        result = activityData as Activity;
      } else {
        // For create flow, we call the API directly
        result = await createActivity(activityData);
      }

      onSubmit(result);
    } catch (err) {
      setError("Failed to save activity");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={!!formErrors.title}
            helperText={formErrors.title}
            disabled={loading}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={4}
            error={!!formErrors.description}
            helperText={formErrors.description}
            disabled={loading}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              value={category}
              label="Category"
              onChange={(e) => setCategory(e.target.value as ActivityCategory)}
              disabled={loading}
            >
              <MenuItem value={ActivityCategory.CHORE}>Chore</MenuItem>
              <MenuItem value={ActivityCategory.ATHLETIC}>Athletic</MenuItem>
              <MenuItem value={ActivityCategory.FINANCIAL}>Financial</MenuItem>
              <MenuItem value={ActivityCategory.CUSTOM}>Custom</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Reward System */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="reward-type-label">Reward Type</InputLabel>
            <Select
              labelId="reward-type-label"
              value={rewardType}
              label="Reward Type"
              onChange={(e) => {
                const newRewardType = e.target.value as RewardType;
                setRewardType(newRewardType);
                // Reset reward amount when switching types
                if (newRewardType === RewardType.POINTS) {
                  setRewardAmount(10); // Default points
                } else {
                  setRewardAmount(1.0); // Default $1.00
                }
              }}
              disabled={loading}
            >
              <MenuItem value={RewardType.POINTS}>
                🏆 Points (Gamification)
              </MenuItem>
              <MenuItem value={RewardType.MONEY}>💰 Money (USD)</MenuItem>
            </Select>
            <FormHelperText>
              Choose whether to reward with points or real money
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={rewardType === RewardType.POINTS ? "Points" : "Amount (USD)"}
            type="number"
            value={rewardAmount}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              setRewardAmount(value);
              // Keep points field in sync for backward compatibility
              if (rewardType === RewardType.POINTS) {
                setPoints(Math.round(value));
              }
            }}
            InputProps={{
              inputProps: {
                min: 0,
                max: rewardType === RewardType.POINTS ? 1000 : 100,
                step: rewardType === RewardType.POINTS ? 1 : 0.25,
              },
              startAdornment: (
                <InputAdornment position="start">
                  {rewardType === RewardType.POINTS ? "🏆" : "$"}
                </InputAdornment>
              ),
            }}
            helperText={
              rewardType === RewardType.POINTS
                ? "Points for completing this activity"
                : "Dollar amount for completing this activity"
            }
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Due Date (Optional)"
              value={dueDate}
              onChange={(newValue) => setDueDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  disabled: loading,
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        {/* Assignment section - only show for parents/teachers/coaches */}
        {user &&
          (user.role === UserRole.PARENT ||
            user.role === UserRole.TEACHER ||
            user.role === UserRole.COACH) && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="assign-to-label">Assign To</InputLabel>
                <Select
                  labelId="assign-to-label"
                  value={assignedTo}
                  label="Assign To"
                  onChange={(e) => setAssignedTo(e.target.value)}
                  disabled={loading}
                >
                  {familyMembers.map((member) => (
                    <MenuItem key={member.userId} value={member.userId}>
                      {member.firstName} {member.lastName}
                      {member.userId === user?.userId && " (You)"} (
                      {member.role.toLowerCase()})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Choose which family member should complete this activity
                </FormHelperText>
              </FormControl>
            </Grid>
          )}

        {/* Recurrence section */}
        <Grid item xs={12}>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Make this a recurring activity"
            />
          </Box>

          <Collapse in={isRecurring}>
            <Box
              sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Recurrence Settings
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="recurrence-type-label">Repeats</InputLabel>
                    <Select
                      labelId="recurrence-type-label"
                      value={recurrenceType}
                      label="Repeats"
                      onChange={(e) =>
                        setRecurrenceType(e.target.value as RecurrenceType)
                      }
                      disabled={loading}
                    >
                      <MenuItem value={RecurrenceType.DAILY}>Daily</MenuItem>
                      <MenuItem value={RecurrenceType.WEEKLY}>Weekly</MenuItem>
                      <MenuItem value={RecurrenceType.MONTHLY}>
                        Monthly
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Repeat every"
                    type="number"
                    value={recurrenceInterval}
                    onChange={(e) =>
                      setRecurrenceInterval(parseInt(e.target.value) || 1)
                    }
                    InputProps={{
                      inputProps: { min: 1, max: 30 },
                      endAdornment: (
                        <InputAdornment position="end">
                          {recurrenceType === RecurrenceType.DAILY
                            ? "days"
                            : recurrenceType === RecurrenceType.WEEKLY
                            ? "weeks"
                            : "months"}
                        </InputAdornment>
                      ),
                    }}
                    disabled={loading}
                  />
                </Grid>

                {recurrenceType === RecurrenceType.WEEKLY && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="day-of-week-label">
                        Day of week
                      </InputLabel>
                      <Select
                        labelId="day-of-week-label"
                        value={dayOfWeek}
                        label="Day of week"
                        onChange={(e) => setDayOfWeek(e.target.value as number)}
                        disabled={loading}
                      >
                        <MenuItem value={0}>Sunday</MenuItem>
                        <MenuItem value={1}>Monday</MenuItem>
                        <MenuItem value={2}>Tuesday</MenuItem>
                        <MenuItem value={3}>Wednesday</MenuItem>
                        <MenuItem value={4}>Thursday</MenuItem>
                        <MenuItem value={5}>Friday</MenuItem>
                        <MenuItem value={6}>Saturday</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Date (Optional)"
                      value={recurrenceEndDate}
                      onChange={(newValue) => setRecurrenceEndDate(newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          disabled: loading,
                          helperText: "Leave blank to repeat indefinitely",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        {onCancel && (
          <Button onClick={onCancel} sx={{ mr: 1 }} disabled={loading}>
            Cancel
          </Button>
        )}

        <Button type="submit" variant="contained" disabled={loading}>
          {loading ? (
            <CircularProgress size={24} />
          ) : activity ? (
            "Update Activity"
          ) : (
            "Create Activity"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ActivityForm;
